import { useState, useEffect, useCallback } from "react";
import { format, parse } from "date-fns";

export function truncate(text, n) {
  return text.length > n ? text.slice(0, n) + "..." : text;
}

export function startingFromString(locations) {
  const location = locations[0];
  let addressString = "";
  let dateString = "";
  let timeString = "";
  if (location.description) {
    addressString = `${truncate(location.description, 22)} ·`;
  } else if (location.address) {
    addressString = `${truncate(location.address, 22)} ·`;
  }

  if (location.date) {
    dateString = `${format(parse(location.date, "yyyy-MM-dd", new Date()), "MMM do yyyy")} ·`;
  }

  if (location.time) {
    timeString = location.time;
  }

  return `${addressString} ${dateString} ${timeString}`;
}

export function endingAtString(locations) {
  const location = locations[1];
  if (location.label) {
    return truncate(location.label, 35);
  } else if (location.description) {
    return truncate(location.description, 35);
  } else if (location.address) {
    return location.address;
  }
  return "";
}

export function generateTimesAMPM() {
  const times = [];
  for (let hour = 0; hour < 24; hour++) {
    for (let min = 0; min < 60; min += 15) {
      const suffix = hour >= 12 ? "PM" : "AM";
      const h12 = hour % 12 === 0 ? 12 : hour % 12;
      const mStr = String(min).padStart(2, "0");
      times.push(`${h12}:${mStr} ${suffix}`);
    }
  }
  return times;
}

export function parseLocalDateTime(dateStr, timeStr) {
  const newDateStr = formatDateToYMD();

  if (!dateStr || !timeStr) return null;

  const [year, month, day] = dateStr.split("-").map(Number);

  const normalized = timeStr.trim().toLowerCase();

  const isPM = normalized.endsWith("pm");
  const isAM = normalized.endsWith("am");

  const timePart = normalized.replace("am", "").replace("pm", "");
  let [hours, minutes] = timePart.split(":").map(Number);

  if (isPM && hours !== 12) hours += 12;
  if (isAM && hours === 12) hours = 0;

  return new Date(year, month - 1, day, hours, minutes);
}

export function parseLocalDate(dateStr) {
  if (!dateStr) return null;

  const [year, month, day] = dateStr.split("-").map(Number);

  return new Date(year, month - 1, day);
}

export function getEarliestAllowedDateTime(
  locations,
  currentIndex,
  bufferMinutes = 10,
) {
  const currentLocation = locations[currentIndex];
  if (!currentLocation) return null;

  for (let i = currentIndex - 1; i >= 0; i--) {
    const prevLoc = locations[i];

    const isValid =
      prevLoc &&
      typeof prevLoc.lat === "number" &&
      typeof prevLoc.lng === "number" &&
      prevLoc.date &&
      prevLoc.time;

    if (!isValid) continue;

    const prevDateTime = parseLocalDateTime(prevLoc.date, prevLoc.time);
    if (!prevDateTime) continue;

    const travelSeconds = currentLocation.travelDurationFromPrevious || 0;

    return new Date(
      prevDateTime.getTime() + travelSeconds * 1000 + bufferMinutes * 60 * 1000,
    );
  }

  return null;
}

export function formatDateToYMD(date) {
  if (!date) return "";
  return `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(
    2,
    "0",
  )}-${String(date.getDate()).padStart(2, "0")}`;
}

export function validateItineraryChronology(locations) {
  let isValid = true;
  let prevDateTime = null;

  locations.forEach((loc, idx) => {
    if (!loc.date || !loc.time) return;

    const currentDateTime = parseLocalDateTime(loc.date, loc.time);
    if (!currentDateTime) return;

    if (prevDateTime && currentDateTime < prevDateTime) {
      isValid = false;
    } else {
      prevDateTime = currentDateTime;
    }
  });

  return { isValid };
}

const normalizeTime = (timeStr) => {
  const [time, modifier] = timeStr.split(" ");

  let [hours, minutes] = time.split(":");

  if (modifier === "PM" && hours !== "12") {
    hours = String(Number(hours) + 12);
  }

  if (modifier === "AM" && hours === "12") {
    hours = "00";
  }

  hours = hours.padStart(2, "0");

  return `${hours}:${minutes}`;
};

export function useSelectScroll(targetValue) {
  const handleDropdownOpen = useCallback(() => {
    let checkCount = 0;

    const checkDOM = () => {
      const items = document.querySelectorAll(
        '.mantine-Select-option, [role="option"], .mantine-Combobox-option',
      );

      const target = Array.from(items).find(
        (el) => el.textContent?.trim() === targetValue.trim(),
      );

      if (target) {
        const scrollableParent =
          target.closest(".mantine-ScrollArea-viewport") ||
          target.closest('[role="listbox"]') ||
          target.closest(".mantine-Select-dropdown");

        if (scrollableParent && scrollableParent.clientHeight > 0) {
          const targetOffset = target.offsetTop;
          const parentHeight = scrollableParent.clientHeight;
          const targetHeight = target.clientHeight;

          const scrollToValue =
            targetOffset - parentHeight / 2 + targetHeight / 2;

          scrollableParent.scrollTop = scrollToValue;

          return;
        }
      }

      if (checkCount < 50) {
        checkCount++;
        requestAnimationFrame(checkDOM);
      }
    };

    checkDOM();
  }, [targetValue]);

  return handleDropdownOpen;
}

export const globalTableReset = `
  #mini-get-a-quote td, 
  #mini-get-a-quote th, 
  #mini-get-a-quote tr, 
  #mini-get-a-quote table {
    border: 0 none transparent !important;
    padding: 0 !important;
    outline: none !important;
    box-shadow: none !important;
    background-image: none !important;
    border-collapse: collapse !important;
    border-spacing: 0 !important;
  }
  
  /* This targets the specific Mantine classes just in case */
  #mini-get-a-quote .mantine-DatePicker-day,
  #mini-get-a-quote .mantine-DatePicker-weekday,
  #mini-get-a-quote .mantine-DatePicker-cell {
    border: 0 none transparent !important;
    background: transparent !important;
  }
`;
